import React, { useState, useMemo } from 'react';
//import "../Pages/css/graph.css"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const prepareData = (rawData, dateBounds) => {
    if (!rawData || !dateBounds.startDate || !dateBounds.endDate) return [];
    
    const dailyMap = {};
    let current = new Date(dateBounds.startDate);

    while (current <= dateBounds.endDate) {
        const label = current.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        dailyMap[label] = { name: label, processing: 0, completed: 0, rejected: 0 };
        current.setDate(current.getDate() + 1);
    }

    rawData.forEach((item) => {
        let itemDate;
        if (item.createdAt && item.createdAt._seconds) {
            itemDate = new Date(item.createdAt._seconds * 1000);
        } else if (item.date) {
            const [day, month, year] = item.date.split('-');
            itemDate = new Date(year, month - 1, day);
        }

        if (itemDate && !isNaN(itemDate) && itemDate >= dateBounds.startDate && itemDate <= dateBounds.endDate) {
            const label = itemDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
            const status = (item.docstatus || '').toLowerCase();
            if (dailyMap[label] && dailyMap[label].hasOwnProperty(status)) {
                dailyMap[label][status]++;
            }
        }
    });

    return Object.values(dailyMap);
};

const UnifiedDashboard = ({ doc = [], ec = [], nl = [] }) => {
    const [range, setRange] = useState('thisMonth');

    // 1. Date Range Logic
    const dateBounds = useMemo(() => {
        const now = new Date();
        let startDate, endDate;
        if (range === 'thisMonth') {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        } else if (range === 'prevMonth') {
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        } else {
            startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }
        return { startDate, endDate };
    }, [range]);

    // 2. Data Transformation (Groups by Date

    const docChartData = useMemo(() => prepareData(doc, dateBounds), [doc, dateBounds]);
const ecChartData = useMemo(() => prepareData(ec, dateBounds), [ec, dateBounds]);
const nlChartData = useMemo(() => prepareData(nl, dateBounds), [nl, dateBounds]);

    // 3. Reusable Chart Sub-Component
    const PerformanceChart = ({ data, title, colorProc, colorComp, colorRej }) => (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #eee', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{title}</h3>
                <div style={{ fontSize: '12px', display: 'flex', gap: '10px' }}>
                    <span><b style={{color: colorProc}}>●</b> Processing</span>
                    <span><b style={{color: colorComp}}>●</b> Completed</span>
                    <span><b style={{color: colorRej}}>●</b> Rejected</span>
                </div>
            </div>
            
            {/* IMPORTANT: Height must be explicitly set here */}
            <div style={{ width: '100%', height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id="colorFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colorProc} stopOpacity={0.2}/>
                                <stop offset="95%" stopColor={colorProc} stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip />
                        <Area type="monotone" dataKey="processing" stroke={colorProc} fill="url(#colorFill)" strokeWidth={2} />
                        <Area type="monotone" dataKey="completed" stroke={colorComp} fill="transparent" strokeWidth={2} />
                        <Area type="monotone" dataKey="rejected" stroke={colorRej} fill="transparent" strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <button style={{ background: 'none', border: 'none', color: '#888', textDecoration: 'underline', fontSize: '13px', cursor: 'pointer', marginTop: '10px' }}>
                Show detailed content
            </button>
        </div>
    );

    return (
        <div style={{ padding: '30px', backgroundColor: '#f9fafb', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '28px' }}>Performance Summary</h1>
                        <p style={{ color: '#666', marginTop: '5px' }}>Monitor your processing trends</p>
                    </div>
                    <select 
                        value={range} 
                        onChange={(e) => setRange(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                        <option value="thisMonth">This Month</option>
                        <option value="prevMonth">Previous Month</option>
                        <option value="last3Months">Last 3 Months</option>
                    </select>
                </div>

                <PerformanceChart data={docChartData} title="Document" colorProc="#3b82f6" colorComp="#10b981" colorRej="#ef4444" />
                <PerformanceChart data={ecChartData} title="Encumbrance Certificate " colorProc="#8b5cf6" colorComp="#10b981" colorRej="#ef4444" />
                <PerformanceChart data={nlChartData} title="Nagal" colorProc="#f59e0b" colorComp="#10b981" colorRej="#ef4444" />
            </div>
        </div>
    );
};

export default UnifiedDashboard;